/**
 * RecordHistoryTimeline — F12.D T12.D.2.
 *
 * Timeline historii zmian rekordu modułu custom.
 * Dane z GET /modules/:moduleId/records/:recordId/history
 *
 * Każdy event = jedna akcja zapisu (create/update/delete/restore).
 * Jedna akcja może zmieniać N pól — wszystkie pokazane w jednym bloku.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Archive, Clock, Edit3, Plus, RefreshCw, User } from 'lucide-react';

import { Button } from '@/components/Button';
import { Spinner } from '@/components/Spinner';
import { api } from '@/lib/api';

// ─── Types ───────────────────────────────────────────────────────────────────

interface FieldChange {
  fieldSlug: string;
  oldValue: string | null;
  newValue: string | null;
}

interface ChangedBy {
  id: string;
  displayName: string | null;
  email: string;
}

interface HistoryEvent {
  changeId: string;
  action: 'create' | 'update' | 'delete' | 'restore';
  changedAt: string;
  changedBy: ChangedBy | null;
  changes: FieldChange[];
}

interface HistoryResponse {
  events: HistoryEvent[];
  total: number;
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface RecordHistoryTimelineProps {
  moduleId: string;
  recordId: string;
  /** Opcjonalna mapa slug → czytelna nazwa pola (z module.fields) */
  fieldNames?: Map<string, string>;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function RecordHistoryTimeline({
  moduleId,
  recordId,
  fieldNames,
}: RecordHistoryTimelineProps) {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const LIMIT = 20;

  const query = useQuery<HistoryResponse>({
    queryKey: ['record-history', moduleId, recordId, offset],
    queryFn: async () => {
      const res = await api.get<HistoryResponse>(
        `/modules/${moduleId}/records/${recordId}/history?limit=${LIMIT}&offset=${offset}`,
      );
      return res.data;
    },
    enabled: !!moduleId && !!recordId,
  });

  if (query.isLoading) {
    return (
      <div className="flex justify-center py-8" data-testid="history-loading">
        <Spinner size="sm" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <p className="text-center text-sm text-red-500 py-4" data-testid="history-error">
        {t('tenant.modules.records.detail.historyError')}
      </p>
    );
  }

  const events = query.data?.events ?? [];
  const total = query.data?.total ?? 0;
  const hasMore = offset + LIMIT < total;
  const hasPrev = offset > 0;

  if (events.length === 0) {
    return (
      <p className="text-center text-sm text-text-tertiary py-8" data-testid="history-empty">
        {t('tenant.modules.records.detail.historyEmpty')}
      </p>
    );
  }

  return (
    <div className="space-y-1" data-testid="record-history-timeline">
      {events.map((event, index) => (
        <HistoryEventItem
          key={event.changeId}
          event={event}
          isLast={index === events.length - 1 && !hasMore}
          fieldNames={fieldNames}
        />
      ))}

      {/* Paginacja */}
      {(hasMore || hasPrev) && (
        <div className="flex items-center justify-between pt-2 text-xs text-text-tertiary">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}
            disabled={!hasPrev}
          >
            {t('tenant.modules.records.pagination.prev')}
          </Button>
          <span>
            {offset + 1}–{Math.min(offset + LIMIT, total)} / {total}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setOffset((o) => o + LIMIT)}
            disabled={!hasMore}
          >
            {t('tenant.modules.records.pagination.next')}
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Single event ─────────────────────────────────────────────────────────────

interface HistoryEventItemProps {
  event: HistoryEvent;
  isLast: boolean;
  fieldNames?: Map<string, string>;
}

function HistoryEventItem({ event, isLast, fieldNames }: HistoryEventItemProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);

  const authorLabel = event.changedBy
    ? (event.changedBy.displayName ?? event.changedBy.email)
    : t('tenant.modules.records.detail.historyUnknownUser');

  const authorInitials = authorLabel
    .split(' ')
    .slice(0, 2)
    .map((s: string) => s[0]?.toUpperCase() ?? '')
    .join('');

  const relativeTime = formatRelativeTime(event.changedAt);

  return (
    <div className="relative flex gap-3" data-testid={`history-event-${event.changeId}`}>
      {/* Vertical line */}
      {!isLast && (
        <div className="absolute left-4 top-8 bottom-0 w-px bg-border-primary" aria-hidden />
      )}

      {/* Avatar / Icon */}
      <div className="relative z-10 mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border-primary bg-bg-secondary text-xs font-semibold text-text-secondary">
        {event.action === 'create' ? (
          <Plus className="h-4 w-4 text-green-600" />
        ) : event.action === 'delete' ? (
          <Archive className="h-4 w-4 text-red-500" />
        ) : event.action === 'restore' ? (
          <RefreshCw className="h-4 w-4 text-amber-600" />
        ) : authorInitials ? (
          <span>{authorInitials}</span>
        ) : (
          <User className="h-4 w-4" />
        )}
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1 pb-4">
        {/* Header row */}
        <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
          <span className="text-sm font-medium text-text-primary">{authorLabel}</span>
          <span className="text-xs text-text-tertiary">{actionLabel(event.action, t)}</span>
          <span
            className="ml-auto flex items-center gap-1 text-xs text-text-tertiary"
            title={new Date(event.changedAt).toLocaleString('pl-PL')}
          >
            <Clock className="h-3 w-3" />
            {relativeTime}
          </span>
        </div>

        {/* Changes list */}
        {event.action === 'update' && event.changes.length > 0 && (
          <div className="mt-1">
            <button
              className="flex items-center gap-1 text-xs text-text-tertiary hover:text-text-secondary"
              onClick={() => setExpanded((v) => !v)}
              data-testid={`history-event-${event.changeId}-toggle`}
            >
              <Edit3 className="h-3 w-3" />
              {expanded
                ? t('tenant.modules.records.detail.historyHideChanges', {
                    count: event.changes.length,
                  })
                : t('tenant.modules.records.detail.historyShowChanges', {
                    count: event.changes.length,
                  })}
            </button>

            {expanded && (
              <ul
                className="mt-1.5 space-y-1 rounded-md bg-bg-secondary px-3 py-2"
                data-testid={`history-event-${event.changeId}-changes`}
              >
                {event.changes.map((ch) => (
                  <li key={ch.fieldSlug} className="text-xs">
                    <span className="font-medium text-text-secondary">
                      {fieldNames?.get(ch.fieldSlug) ?? ch.fieldSlug}
                    </span>
                    {': '}
                    {ch.oldValue != null ? (
                      <>
                        <span className="rounded bg-red-100 px-1 text-red-700 line-through dark:bg-red-900/20 dark:text-red-400">
                          {truncate(ch.oldValue)}
                        </span>
                        {' → '}
                      </>
                    ) : null}
                    {ch.newValue != null ? (
                      <span className="rounded bg-green-100 px-1 text-green-700 dark:bg-green-900/20 dark:text-green-400">
                        {truncate(ch.newValue)}
                      </span>
                    ) : (
                      <span className="text-text-tertiary">—</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function actionLabel(action: string, t: (key: string) => string): string {
  switch (action) {
    case 'create':
      return t('tenant.modules.records.detail.historyActionCreate');
    case 'update':
      return t('tenant.modules.records.detail.historyActionUpdate');
    case 'delete':
      return t('tenant.modules.records.detail.historyActionDelete');
    case 'restore':
      return t('tenant.modules.records.detail.historyActionRestore');
    default:
      return action;
  }
}

function truncate(value: string, max = 80): string {
  return value.length > max ? value.slice(0, max) + '…' : value;
}

function formatRelativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return 'przed chwilą';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min temu`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} godz. temu`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} dni temu`;
  return new Date(isoString).toLocaleDateString('pl-PL');
}
